import { useUpdateBriefOrganizationAllocation } from "@entities/brief/lib/hooks/useUpdateBriefOrganizationAllocation";
import { useSearchBrandUser } from "@entities/organisation/lib/hooks/useSearchBrandUser";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ACCOUNT_STATUS } from "@shared/lib/types";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

export type CampaignManagerRow = {
  id: string;
  name: string;
  designation: string;
  email: string;
};

export function getManagerInitials(name: string) {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function useCampaignManagers(options?: { searchEnabled?: boolean }) {
  const { brief, briefId } = useBriefStore();

  const [managerSearch, setManagerSearch] = useState("");
  const [debouncedSearch] = useDebounce(managerSearch, 400);

  const {
    updateBriefOrganizationAllocation,
    isUpdatingOrganizationAllocation,
  } = useUpdateBriefOrganizationAllocation();

  const selectedIds = useMemo(
    () => brief?.brandOrganisationManagers?.map((m) => m.userId) ?? [],
    [brief?.brandOrganisationManagers]
  );

  const searchEnabled =
    options?.searchEnabled !== undefined
      ? options.searchEnabled && !!brief?.brandId
      : !!brief?.brandId;

  // const organisationId = brief?.oe?._id;

  const { employeesData, isFetchingEmployees } = useSearchBrandUser({
    searchQuery: debouncedSearch || undefined,
    brandIds: brief?.brandId ? [brief.brandId] : [],
    limit: 100,
    pageNo: 1,
    brandOrganisationId: brief?.brand?.brandOrganisationId || "",
    isEnabled: searchEnabled,
    accountStatuses: [ACCOUNT_STATUS.ACTIVE],
  });

  const employees = employeesData?.data?.data ?? [];

  const selectedManagers = useMemo((): CampaignManagerRow[] => {
    const rows: CampaignManagerRow[] = [];

    for (const m of brief?.brandOrganisationManagers ?? []) {
      rows.push({
        id: m.userId,
        name: `${m.user.firstName} ${m.user.lastName}`.trim(),
        designation: m.user.role || "",
        email: m.user?.emailId || "",
      });
    }

    return rows;
  }, [brief?.brandOrganisationManagers]);

  const availableToAdd = useMemo(() => {
    return employees.filter((u) => !selectedIds.includes(u._id));
  }, [employees, selectedIds]);

  const persistManagers = useCallback(
    (nextIds: string[]) => {
      if (!briefId) return;
      updateBriefOrganizationAllocation({
        briefId,
        brandOrganisationManagers: nextIds,
      });
    },
    [briefId, updateBriefOrganizationAllocation]
  );

  const handleAdd = useCallback(
    (userId: string) => {
      if (selectedIds.includes(userId) || isUpdatingOrganizationAllocation) {
        return;
      }
      persistManagers([...selectedIds, userId]);
    },
    [isUpdatingOrganizationAllocation, persistManagers, selectedIds]
  );

  const handleRemove = useCallback(
    (userId: string) => {
      if (isUpdatingOrganizationAllocation) return;
      persistManagers(selectedIds.filter((id) => id !== userId));
    },
    [isUpdatingOrganizationAllocation, persistManagers, selectedIds]
  );

  const clearSearch = useCallback(() => {
    setManagerSearch("");
  }, []);

  return {
    managerSearch,
    setManagerSearch,
    clearSearch,
    selectedIds,
    selectedManagers,
    availableToAdd,
    isFetchingEmployees,
    isUpdatingOrganizationAllocation,
    handleAdd,
    handleRemove,
  };
}
